import { useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import clsx from 'clsx';
import { useSampleProjects } from '@hooks/useSampleProjects';
import { SampleProjectsApi } from '@pages/api/project/sample';
import { UserType } from '@utils/types';
import { parseImage } from '@utils/image';
import { Tilt } from '@components/Tilt';
import { Tag } from '@components/Tag';
import { getRewardString } from '@utils/token';

export function SampleProjects() {
  const { data, isValidating } = useSampleProjects();

  if (!isValidating && !data?.projects.length)
    return <div>No projects exist yet. Create the first one!</div>;

  return <Projects projects={data?.projects} userType={null} getLink={() => ''} />;
}

export function Projects(props: {
  projects: SampleProjectsApi['projects'] | undefined;
  getLink: (slug: string) => string;
  userType: UserType | null;
}) {
  const { projects, userType, getLink } = props;

  return (
    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
      {projects?.map(project => {
        return <Project key={project.id} project={project} userType={userType} getLink={getLink} />;
      })}
    </div>
  );
}

export function Project(props: {
  project: SampleProjectsApi['projects'][number];
  getLink: (slug: string) => string;
  userType: UserType | null;
}) {
  const { project, userType, getLink } = props;

  const shortenedDescription = project.oneLiner || project.description.substring(0, 100);

  const coverImage = useMemo(() => parseImage(project.coverPictureUrl), [project.coverPictureUrl]);

  return (
    <Tilt
      className={clsx('relative flex flex-col rounded-lg bg-white shadow', userType ? '' : 'p-6')}
    >
      <Link href={getLink(project.slug)}>
        <div className={clsx(userType ? 'flex-1 px-6 pb-2 pt-6' : '')}>
          <div className="absolute left-0 right-0 top-0">
            {!project.coverPictureUrl ? (
              <div className={'inline-flex h-16 w-full rounded-t-lg bg-blue-50 p-3 ring-white'} />
            ) : (
              <div className="relative h-16">
                <div
                  className={clsx(
                    'absolute inset-0 rounded-t-lg',
                    coverImage.gradient?.direction,
                    coverImage.gradient?.colors
                  )}
                  style={{ backgroundColor: coverImage.color }}
                />
                <Image
                  src={coverImage.imageUrl}
                  alt=""
                  className="w-full rounded-t-lg object-cover"
                  fill
                />
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <div className="relative h-16 w-16">
              {!project.profilePictureUrl ? (
                <div className="inline-flex h-16 w-16 rounded-full bg-blue-50 p-3 ring-4 ring-white" />
              ) : (
                <Image src={project.profilePictureUrl} alt="" className="rounded-full" fill />
              )}
            </div>

            {getRewardString(project.affiliateReward) ? (
              <div className="relative right-0 top-6 z-10">
                <Tag>{getRewardString(project.affiliateReward)}</Tag>
              </div>
            ) : null}
          </div>

          <div className="mt-4">
            <h3 className="text-lg font-medium">{project.name}</h3>
            <p className="mt-1 flex-1 text-sm text-gray-500">
              {shortenedDescription}
              {project.oneLiner || shortenedDescription.length < 100 ? '' : '...'}
            </p>
          </div>
        </div>

        {/* {showActions ? (
          <>
            {userType === 'affiliate' &&
              (props.showJoin ? (
                <CardButtons buttons={[{ label: 'Join', href: `/${project.slug}/sharer` }]} />
              ) : (
                <CardButtons buttons={[{ label: 'View', href: `/${project.slug}/sharer` }]} />
              ))}
            {userType === 'creator' && (
              <CardButtons
                buttons={[
                  { label: 'View', href: `/${project.slug}/dashboard` },
                  // { label: 'Admin', href: `/${project.slug}/dashboard` },
                  // { label: 'Affiliate Page', href: `/${project.slug}/sharer` },
                ]}
              />
            )}
          </>
        ) : (
          <div className="pb-4" />
        )} */}

        <div className="pb-4" />
      </Link>
    </Tilt>
  );
}

function CardButtons(props: { buttons: { label: string; href: string }[] }) {
  return (
    <div
      className={clsx(
        'mt-2 grid space-x-2 border-t border-gray-100 text-center text-sm text-gray-700',
        props.buttons.length === 2 && 'grid-cols-2'
      )}
    >
      {props.buttons.map(button => (
        <div key={button.label} className="hover:bg-gray-50">
          <Link href={button.href} className="block cursor-pointer p-3 hover:text-gray-900">
            {button.label}
          </Link>
        </div>
      ))}
    </div>
  );
}
